import { setLogoutInProgress } from "@auth/keycloakClient";
import { LOCAL_STORAGE, COOKIES } from "@constants/index";
import { basename } from "@router/index";
import { postServerLogout } from "./auth";
import { removeCookie } from "./common";
import { removeLocalStorage } from "./local-storage";
import { clearAllIndexedDbData } from "./indexedDb";

export function getRedirectPathBeforeLogin() {
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath.includes("/login")) {
    return null;
  }

  const baseName = basename && basename !== "/" ? basename : "";
  let pathToStore = currentPath;
  if (baseName && currentPath.startsWith(baseName)) {
    pathToStore = currentPath.substring(baseName.length) || "/";
  }
  return pathToStore;
}

/**
 * Drop IndexedDB + sync flag left behind after a previous session.
 * Needed when the browser is closed without logout: session cookie is gone
 * but localStorage/IndexedDB still hold the previous user's cached lists.
 * Call on every successful login so the next mount re-fetches APIs.
 */
export async function resetCachedUserData() {
  localStorage.removeItem(LOCAL_STORAGE.SYNCED_ALL_DATA);
  try {
    await clearAllIndexedDbData();
  } catch (error) {
    console.warn("Failed to clear IndexedDB data on login:", error);
  }
}

export async function clearAuthSession({
  preserveRedirectPath = false,
  redirectPath = null,
  clearIndexedDb = false,
} = {}) {
  const shouldPreserveRedirect = preserveRedirectPath && redirectPath;

  if (shouldPreserveRedirect) {
    localStorage.setItem(LOCAL_STORAGE.REDIRECT_AFTER_LOGIN, redirectPath);
  }

  const storageKeysToRemove = [
    LOCAL_STORAGE.TOKEN,
    LOCAL_STORAGE.SESSION_ID,
    LOCAL_STORAGE.ACCESS_TOKEN,
    "refreshToken",
    LOCAL_STORAGE.USER_ID,
    LOCAL_STORAGE.SYNCED_ALL_DATA,
    LOCAL_STORAGE.LAST_RECONCILE_AT,
    LOCAL_STORAGE.PERMISSIONS,
    LOCAL_STORAGE.JUST_LOGGED_IN,
    LOCAL_STORAGE.USE_IMPORT_COMPONENT,
    LOCAL_STORAGE.USE_PREVIEW,
    LOCAL_STORAGE.USE_VEX,
    LOCAL_STORAGE.IS_SKIP_CLARITY_PROGRESS,
  ];

  if (!shouldPreserveRedirect) {
    storageKeysToRemove.push(LOCAL_STORAGE.REDIRECT_AFTER_LOGIN);
  }

  removeLocalStorage(storageKeysToRemove);
  removeCookie(COOKIES.LOGIN_STATUS);

  if (clearIndexedDb) {
    try {
      await clearAllIndexedDbData();
    } catch (error) {
      console.warn("Failed to clear IndexedDB data:", error);
    }
  }
}

export function redirectToExpiredLogin() {
  const baseName = basename && basename !== "/" ? basename : "";
  const redirectTo = `${baseName}/login?expired=true`.replace(/\/+/g, "/");
  window.location.href = redirectTo;
}

/**
 * Confirmed session end.
 * If SESSION_ID already moved to a different value (another tab won refresh), skip clear.
 *
 * @param {{ sessionIdToRevoke?: string|null }} [options]
 */
export async function logoutOnSessionExpired(options = {}) {
  const { sessionIdToRevoke } = options;
  setLogoutInProgress(true);
  let redirectStarted = false;

  try {
    const currentSessionId = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);

    if (
      sessionIdToRevoke &&
      currentSessionId &&
      currentSessionId !== sessionIdToRevoke
    ) {
      return false;
    }

    const sessionIdForServer = sessionIdToRevoke || currentSessionId;
    postServerLogout(sessionIdForServer).catch(() => {});

    const sessionAfter = localStorage.getItem(LOCAL_STORAGE.SESSION_ID);
    if (
      sessionIdToRevoke &&
      sessionAfter &&
      sessionAfter !== sessionIdToRevoke
    ) {
      return false;
    }

    await clearAuthSession({
      preserveRedirectPath: true,
      redirectPath: getRedirectPathBeforeLogin(),
      clearIndexedDb: true,
    });
    redirectToExpiredLogin();
    redirectStarted = true;
    return true;
  } finally {
    if (!redirectStarted) {
      setLogoutInProgress(false);
    }
  }
}
